import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MONO, fmt, fmtPct, pctDelta } from './format'

interface MetricDeltasProps {
  current: number
  /** Immediately preceding period's value, or null if it isn't in the dataset. */
  prevMonth: number | null
  /** Same period a year earlier, or null if it isn't in the dataset. */
  prevYear: number | null
  /** Label for the YoY period, e.g. "May 2025" or "Mar – May 2025". */
  prevYearLabel: string
  currency: string
  size?: 'sm' | 'lg'
  /** Label for the prior comparison, e.g. "prior month" or "prior period". */
  prevLabel?: string
  /** Currency figures (default) or a percentage rate. */
  unit?: 'currency' | 'percent'
}

const GREY = 'text-stone-300 dark:text-stone-600'

/** A single comparison line: arrow + signed % + label (+ optional absolute). */
function DeltaLine({
  current,
  prev,
  label,
  showAbs,
  fmtVal,
  size,
}: {
  current: number
  prev: number
  label: string
  showAbs?: boolean
  fmtVal: (n: number) => string
  size: 'sm' | 'lg'
}) {
  const text = size === 'lg' ? 'text-sm' : 'text-[11px] leading-tight'
  const iconSz = size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'
  // Guard divide-by-zero: no meaningful % when the comparison period is zero.
  if (!prev) {
    return (
      <p className={cn(text, GREY)}>
        n/a {label}
        {showAbs ? ` (${fmtVal(prev)})` : ''}
      </p>
    )
  }
  const pct = pctDelta(current, prev)
  const up = pct >= 0
  const Arrow = up ? ArrowUpRight : ArrowDownRight
  return (
    <p className={cn('font-medium', text, up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
      <Arrow className={cn(iconSz, 'mr-0.5 inline-block align-[-0.15em]')} aria-hidden />
      {fmtPct(pct)}{' '}
      <span className="font-normal text-stone-400 dark:text-stone-500">
        {label}
        {showAbs ? <> (<span className={MONO}>{fmtVal(prev)}</span>)</> : null}
      </span>
    </p>
  )
}

/**
 * Two stacked comparisons for a headline figure: prior period first, then
 * year-over-year (with the prior-year absolute). Missing comparisons grey out.
 */
export function MetricDeltas({ current, prevMonth, prevYear, prevYearLabel, currency, size = 'sm', prevLabel = 'prior month', unit = 'currency' }: MetricDeltasProps) {
  const text = size === 'lg' ? 'text-sm' : 'text-[11px] leading-tight'
  const fmtVal = unit === 'percent' ? (n: number) => `${Math.round(n)}%` : (n: number) => fmt(n, currency)
  return (
    <div className="mt-1.5 space-y-1">
      {prevMonth != null ? (
        <DeltaLine current={current} prev={prevMonth} label={`vs ${prevLabel}`} fmtVal={fmtVal} size={size} />
      ) : (
        <p className={cn(text, GREY)}>No {prevLabel}</p>
      )}
      {prevYear != null ? (
        <DeltaLine current={current} prev={prevYear} label={`vs ${prevYearLabel}`} showAbs fmtVal={fmtVal} size={size} />
      ) : (
        <p className={cn(text, GREY)}>No prior-year data</p>
      )}
    </div>
  )
}
